import { Link } from "react-router-dom";
import {
  Check,
  CircleDashed,
  FileText,
  Globe,
  Hand,
  Image,
  Loader2,
  Mail,
  MessageCircle,
  Send,
  Share2,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { TournamentStatus } from "@/lib/api";

type StepState =
  | "pending" // non ancora iniziato
  | "ready" // prossimo step eseguibile
  | "running" // in esecuzione (infer da status)
  | "done"
  | "await" // checkpoint in attesa di approvazione
  | "failed";

type StepDef = {
  id: number | string;
  label: string;
  hint?: string;
  icon: React.ComponentType<{ className?: string }>;
  checkpoint?: boolean;
  /** Slot per ruotta azione quando lo step è attivo */
  link?: (slug: string) => string | null;
};

export const STEPS: StepDef[] = [
  { id: 1, label: "Testi", hint: "IG, FB, WA, Email", icon: FileText },
  {
    id: 2,
    label: "Approva testi",
    hint: "checkpoint manuale",
    icon: Hand,
    checkpoint: true,
    link: (slug) => `/tournaments/${slug}/checkpoint/texts`,
  },
  { id: 3, label: "Locandina", hint: "3 formati NB2", icon: Image },
  {
    id: 4,
    label: "Approva locandina",
    hint: "checkpoint manuale",
    icon: Hand,
    checkpoint: true,
    link: (slug) => `/tournaments/${slug}/checkpoint/flyer`,
  },
  { id: 7, label: "Landing", hint: "pagina web", icon: Globe },
  { id: 5, label: "Email", hint: "composizione HTML", icon: Mail },
  { id: 6, label: "Invio email", hint: "~6322 destinatari", icon: Send },
  { id: 8, label: "WhatsApp", hint: "~5922 destinatari", icon: MessageCircle },
  { id: 9, label: "Social", hint: "daily cron", icon: Share2 },
];

/**
 * Deriva lo stato di ogni step dal TournamentStatus.
 * L'ordine di progressione: 1 → 2 → 3 → 4 → 7 → 5 → 6 → 8 → 9
 */
/** Normalizza lo status backend (può arrivare sia UPPERCASE sia lowercase). */
export function normalizeStatus(raw: unknown): string {
  if (typeof raw !== "string") return "DRAFT";
  return raw.toUpperCase();
}

export function computeStepStates(
  statusRaw: TournamentStatus | string,
): Record<number, StepState> {
  const status = normalizeStatus(statusRaw);
  const s: Record<number, StepState> = {
    1: "pending",
    2: "pending",
    3: "pending",
    4: "pending",
    7: "pending",
    5: "pending",
    6: "pending",
    8: "pending",
    9: "pending",
  };

  // Progressione
  const progress: Record<string, number[]> = {
    DRAFT: [],
    TEXTS_GENERATED: [1],
    TEXTS_APPROVED: [1, 2],
    FLYER_GENERATED: [1, 2, 3],
    FLYER_APPROVED: [1, 2, 3, 4],
    LANDING_DEPLOYED: [1, 2, 3, 4, 7],
    EMAIL_COMPOSED: [1, 2, 3, 4, 7, 5],
    EMAILS_SENT: [1, 2, 3, 4, 7, 5, 6],
    WHATSAPP_SENT: [1, 2, 3, 4, 7, 5, 6, 8],
    SOCIAL_RUNNING: [1, 2, 3, 4, 7, 5, 6, 8],
    COMPLETED: [1, 2, 3, 4, 7, 5, 6, 8, 9],
  };

  const done = progress[status] ?? [];
  done.forEach((id) => (s[id] = "done"));

  // Segna il prossimo step come "ready" / "await"
  const sequence = [1, 2, 3, 4, 7, 5, 6, 8, 9];
  const nextIdx = sequence.findIndex((id) => s[id] === "pending");
  if (nextIdx >= 0) {
    const nextId = sequence[nextIdx];
    const def = STEPS.find((st) => st.id === nextId);
    s[nextId] = def?.checkpoint ? "await" : "ready";
  }

  if (status === "SOCIAL_RUNNING") s[9] = "running";
  if (status === "FAILED") {
    const idx = done.length;
    if (idx < sequence.length) s[sequence[idx]] = "failed";
  }

  return s;
}

const STATE_STYLES: Record<StepState, { ring: string; bg: string; text: string; icon: React.ComponentType<{ className?: string }> | null }> = {
  pending: {
    ring: "ring-slate-200",
    bg: "bg-white",
    text: "text-slate-400",
    icon: null,
  },
  ready: {
    ring: "ring-court-500 ring-2",
    bg: "bg-court-50",
    text: "text-court-700",
    icon: CircleDashed,
  },
  running: {
    ring: "ring-moving-500 ring-2",
    bg: "bg-moving-50",
    text: "text-moving-700",
    icon: Loader2,
  },
  done: {
    ring: "ring-moving-500",
    bg: "bg-moving-500",
    text: "text-white",
    icon: Check,
  },
  await: {
    ring: "ring-amber-500 ring-2",
    bg: "bg-amber-50",
    text: "text-amber-700",
    icon: Hand,
  },
  failed: {
    ring: "ring-red-500",
    bg: "bg-red-50",
    text: "text-red-700",
    icon: X,
  },
};

export function PipelineStepper({
  slug,
  status,
  onStepClick,
}: {
  slug: string;
  status: TournamentStatus | string;
  onStepClick?: (step: number) => void;
}) {
  const states = computeStepStates(status);

  return (
    <div className="overflow-x-auto">
      <ol className="flex min-w-max items-start gap-0 py-1">
        {STEPS.map((step, i) => {
          const state = states[step.id as number] ?? "pending";
          const st = STATE_STYLES[state];
          const Icon = st.icon ?? step.icon;
          const spin = state === "running";
          const isCheckpointLink =
            step.checkpoint && (state === "await" || state === "done");

          const content = (
            <div className="flex min-w-[110px] flex-col items-center gap-1.5">
              <div
                className={cn(
                  "relative flex h-10 w-10 items-center justify-center rounded-full ring-1 transition-all",
                  st.bg,
                  st.ring,
                )}
              >
                <Icon
                  className={cn(
                    "h-4 w-4",
                    st.text,
                    spin ? "animate-spin" : "",
                  )}
                />
                <span
                  className={cn(
                    "absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold",
                    state === "done"
                      ? "bg-moving-700 text-white"
                      : state === "ready"
                        ? "bg-court-500 text-white"
                        : state === "await"
                          ? "bg-amber-500 text-white"
                          : state === "failed"
                            ? "bg-red-500 text-white"
                            : "bg-slate-200 text-slate-500",
                  )}
                >
                  {step.id}
                </span>
              </div>
              <div className="text-center">
                <div
                  className={cn(
                    "text-xs font-medium",
                    state === "pending"
                      ? "text-slate-400"
                      : state === "failed"
                        ? "text-red-700"
                        : state === "await"
                          ? "text-amber-700"
                          : state === "done"
                            ? "text-slate-900"
                            : "text-slate-900",
                  )}
                >
                  {step.label}
                </div>
                {step.hint ? (
                  <div className="text-[10px] text-slate-400">{step.hint}</div>
                ) : null}
              </div>
            </div>
          );

          return (
            <li
              key={step.id}
              className="relative flex items-start gap-0"
              aria-label={`Step ${step.id}: ${step.label} · ${state}`}
            >
              {isCheckpointLink && step.link ? (
                <Link
                  to={step.link(slug)!}
                  className="rounded-md hover:bg-slate-50"
                >
                  {content}
                </Link>
              ) : onStepClick && typeof step.id === "number" ? (
                <button
                  type="button"
                  onClick={() => onStepClick(step.id as number)}
                  className="rounded-md hover:bg-slate-50"
                >
                  {content}
                </button>
              ) : (
                <div>{content}</div>
              )}

              {i < STEPS.length - 1 ? (
                <div
                  className={cn(
                    "mt-5 h-px flex-1 min-w-[24px]",
                    states[STEPS[i + 1].id as number] === "pending"
                      ? "bg-slate-200"
                      : "bg-moving-500",
                  )}
                />
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
